import { apiRequest } from '../config/api';

export interface SearchMember {
  id: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  chapter?: string;
  province?: string;
  photoUrl?: string;
}

export interface SearchItem {
  id: string;
  title: string;
  subtitle?: string;
  slug?: string | null;
  /** Newsletters: 'published' issues have a public page, others do not. */
  status?: string | null;
  date?: string | null;
  publishedAt?: string | null;
  coverImageUrl?: string | null;
}

export interface GlobalSearchResults {
  members: SearchMember[];
  events: SearchItem[];
  groups: SearchItem[];
  newsletters: SearchItem[];
  surveys: SearchItem[];
}

const EMPTY: GlobalSearchResults = {
  members: [],
  events: [],
  groups: [],
  newsletters: [],
  surveys: [],
};

/**
 * Global search across members, events, groups, newsletters and surveys.
 * Members come from the proven org-scoped directory search; everything else
 * from the /api/search/global aggregator. Both run in parallel.
 */
export async function globalSearch(query: string, limit = 5): Promise<GlobalSearchResults> {
  const q = (query || '').trim();
  if (q.length < 2) return EMPTY;
  const encoded = encodeURIComponent(q);

  const [membersRes, aggRes] = await Promise.allSettled([
    apiRequest<{ members?: SearchMember[] }>(
      `/api/members/directory/search?q=${encoded}&limit=${limit}`,
    ),
    apiRequest<{ results?: Partial<GlobalSearchResults> }>(
      `/api/search/global?q=${encoded}&limit=${limit}`,
    ),
  ]);

  const members = membersRes.status === 'fulfilled' ? membersRes.value.members || [] : [];
  const agg = aggRes.status === 'fulfilled' ? aggRes.value.results || {} : {};

  return {
    members,
    events: agg.events || [],
    groups: agg.groups || [],
    newsletters: agg.newsletters || [],
    surveys: agg.surveys || [],
  };
}

export function totalResultCount(r: GlobalSearchResults): number {
  return (
    r.members.length + r.events.length + r.groups.length + r.newsletters.length + r.surveys.length
  );
}
